import * as React from 'react';
import styled from 'styled-components';
import * as numeral from 'numeral';
import { colors } from 'kaizen-ui-colors/lib';
import { ExperienceStateType, IExperience, IRoundInfo, stateLabel } from '../../domain/entity/PortfolioReportItem';
import Layout, * as Area from './ExperienceTableGridLayout';
import { CvCoverageBar } from './CvCoverageBar';
import { RoundInfoDetail } from './RoundInfoDetail';
import { RoundTimeline } from './RoundTimeline';
import { Text } from './Text';
import config from '../../config';
import { elevation0, elevation3 } from '../sharedStyle/elevation';
import { easeInOut } from '../sharedStyle/animation';

interface IProps {
  experience: IExperience;
  reportFrom: Date;
  selectedGoalId: string;
}

interface IState {
  notePanelState: NotePanelState;
}

enum NotePanelState {
  Open,
  Close,
}

const Wrapper = styled.div`
  border-radius: 2px;
  background-color: #fff;
  ${elevation0};
  margin-bottom: 8px;
  transition: box-shadow 150ms ${easeInOut};
  &[data-hovarable='true']:hover {
    ${elevation3};
  }
`;

const Cell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  height: 100%;
`;

const ExperienceId = styled.span`
  display: inline-block;
  font-size: 0.923rem;
  color: ${colors.ash__b4bed5};
  margin-bottom: 4px;
`;

const ExperienceTitle = styled.span`
  display: inline-block;
  font-weight: 700;
  color: ${colors.slate__4c566c};
  &:hover {
    text-decoration: underline;
  }
`;

export class ExperienceTableRow extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      notePanelState: NotePanelState.Close,
    };
    this.toggleVariationNotePanel = this.toggleVariationNotePanel.bind(this);
  }

  private renderExperience(experience: IExperience) {
    return (
      <div style={{ maxWidth: '280px', whiteSpace: 'initial' }}>
        <div>
          <ExperienceId>#{experience.id}</ExperienceId>
        </div>
        <a href={`${config.dashboard}/experiments/${experience.id}`} target="_blank">
          <ExperienceTitle>{experience.title}</ExperienceTitle>
        </a>
      </div>
    );
  }
  private renderCvCoverage(cvRatio: number | null) {
    if (cvRatio) {
      return (
        <div style={{ display: 'flex', width: '100%' }}>
          <div style={{ width: '100px', lineHeight: '0' }}>
            <CvCoverageBar ratio={cvRatio} />
          </div>
          <Text size="tiny" style={{ marginLeft: '1em' }} numeric>
            {numeral(cvRatio).format('0.0')}%
          </Text>
        </div>
      );
    } else {
      return (
        <div style={{ width: '100%', textAlign: 'center' }}>
          <Text size="small">（CVなし）</Text>
        </div>
      );
    }
  }
  private renderState(state: ExperienceStateType) {
    if (state === 'competition') {
      return (
        <Text size={'small'}>
          <strong>{stateLabel(state)}</strong>
        </Text>
      );
    } else {
      return <Text size={'small'}>{stateLabel(state)}</Text>;
    }
  }
  private renderImprovementRate(improvementRate: number) {
    const color =
      improvementRate && improvementRate > 100
        ? colors.veggie__2dcc70
        : improvementRate < 100 ? colors.lava__fe7877 : 'inherit';
    return (
      <Text numeric>
        <span style={{ color }}>{numeral(improvementRate).format('0.0')}%</span>
      </Text>
    );
  }
  private renderDailyCv(dailyCv: number) {
    return <span>{numeral(dailyCv).format('0,0')}</span>;
  }
  private renderDailyVisit(dailyVisit: number) {
    return <span>{numeral(dailyVisit).format('0,0')}</span>;
  }
  private renderDailyCvr(dailyCvr: number) {
    return <span>{numeral(dailyCvr).format('0.0')}%</span>;
  }
  private renderRoundTimeline(start: Date, rounds: any[]) {
    return (
      <div style={{ height: '32px', paddingLeft: '12px' }}>
        <RoundTimeline start={start} rounds={rounds} />
      </div>
    );
  }
  private toggleVariationNotePanel() {
    if (this.state.notePanelState === NotePanelState.Open) {
      this.setState({
        notePanelState: NotePanelState.Close,
      });
    } else {
      this.setState({
        notePanelState: NotePanelState.Open,
      });
    }
  }
  public render() {
    const { experience, reportFrom, selectedGoalId } = this.props;
    const rounds = experience.rounds.filter((round: IRoundInfo) => round.type === 'competition');
    return (
      <Wrapper data-hovarable={rounds.length > 0}>
        <Layout
          style={{
            cursor: rounds.length > 0 ? 'pointer' : 'inherit',
          }}
          onClick={rounds.length ? this.toggleVariationNotePanel : () => {}}
        >
          <Area.Experience>
            <Cell style={{ justifyContent: 'flex-start' }}>{this.renderExperience(experience)}</Cell>
          </Area.Experience>
          <Area.CvCoverage>
            <Cell style={{ justifyContent: 'flex-start' }}>{this.renderCvCoverage(experience.cvCoverageRatio)}</Cell>
          </Area.CvCoverage>
          <Area.Status>
            <Cell>
              <Text>{this.renderState(experience.state)}</Text>
            </Cell>
          </Area.Status>
          <Area.ImprovementRate>
            <Cell style={{ justifyContent: 'flex-end' }}>
              <Text numeric>{this.renderImprovementRate(experience.totalImprovementRatio)}</Text>
            </Cell>
          </Area.ImprovementRate>
          <Area.AverageCv>
            <Cell style={{ justifyContent: 'flex-end' }}>
              <Text numeric>{this.renderDailyCv(experience.actualAverageCv)}</Text>
            </Cell>
          </Area.AverageCv>
          <Area.AverageVisit>
            <Cell style={{ justifyContent: 'flex-end' }}>
              <Text numeric>{this.renderDailyVisit(experience.actualAveragePv)}</Text>
            </Cell>
          </Area.AverageVisit>
          <Area.AverageCvr>
            <Cell style={{ justifyContent: 'flex-end' }}>
              <Text numeric>{this.renderDailyCvr(experience.actualAverageCvr)}</Text>
            </Cell>
          </Area.AverageCvr>
          <Area.RoundTimeline>
            <Cell>
              <Text style={{ width: '100%' }}>{this.renderRoundTimeline(reportFrom, experience.rounds)}</Text>
            </Cell>
          </Area.RoundTimeline>
        </Layout>
        <div
          style={{
            display: this.state.notePanelState === NotePanelState.Open ? 'block' : 'none',
            overflow: 'hidden',
            borderTop: `1px solid ${colors.lithium__dbe0eb}`,
            backgroundColor: `${colors.mochi_lighter__f9fafd}`,
          }}
        >
          <div style={{ height: this.state.notePanelState === NotePanelState.Open ? 'auto' : 0 }}>
            {rounds.map((round: IRoundInfo) => (
              <RoundInfoDetail key={round.id} round={round} experience={experience} selectedGoalId={selectedGoalId} />
            ))}
          </div>
        </div>
      </Wrapper>
    );
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/presentation/component/ExperienceTableRow.tsx